/**
 * Created by Andrey Popov on 11/12/25.
 */

// Can only do add/remove/move/getRowByIndex sync
// rm all async, rm pinnedRow
// all animations in ScrollTable (wait until row animates and then remove it)
const TableHandler = function (table, scrollView, options) {
    this.table = table;
    this.scrollView = scrollView;
    this.container = scrollView.innerContent;

    this.options = options;
    this.styles = options.styles || cleverapps.styles.DefaultTableStyles;

    this.init();
};

TableHandler.prototype.init = function () {
    this.options.rowOptions = this.options.rowOptions || {};
    this.options.rowOptions.clickableArea = this.scrollView;

    this.scrollView.setDirection(this.table.getDirection() === cleverapps.UI.VERTICAL ? cleverapps.UI.ScrollView.DIR_VERTICAL : cleverapps.UI.ScrollView.DIR_HORIZONTAL);

    this.rowViews = new Map();

    this.table.getRows().forEach((row, index) => {
        const rowView = this.createRow(row);
        this.rowViews.set(row.id, rowView);
        this.container.addChild(rowView, row.pinned ? 1000 : -index);
    });

    if (this.options.countOfRowsToSnap) {
        this.scrollView.scrollHandler.setSnapHandler((isMoveToStart) => {
            const newAnchorRowIndex = this._getAnchorRowIndex() + this.options.countOfRowsToSnap * (isMoveToStart ? 1 : -1);
            return this._computeDeltaToPlaceRowAsAnchor(newAnchorRowIndex);
        }, 0.6 * this.options.countOfRowsToSnap);
    }

    this.table.on("showUp", this.showUp.bind(this), this);
    this.table.on("add", this.add.bind(this), this);
    this.table.on("remove", this.remove.bind(this), this);
    this.table.on("move", this.move.bind(this), this);
    this.table.on("updateRow", this.updateRow.bind(this), this);
    this.table.on("scrollToRow", this.scrollToRow.bind(this), this);
    this.table.on("stop", this.stop.bind(this), this);
    this.table.on("reorder", () => {
        cleverapps.effects.fire("ui.table.swap");
    }, this);
    this.table.on("toggleTouchScroll", (enabled) => {
        this.scrollView.touchScrollDisabled = !enabled;
    }, this);
    this.table.on("onRowRemoved", this.onRowRemoved.bind(this), this);

    this.table.onGetView = this.scrollView.createListener(() => this.scrollView);
    this.table.getRowView = this.scrollView.createListener((rowId) => this.rowViews.get(rowId));
    this.scrollView.onUpdatePositionListener = this.scrollView.createListener(this.updatePinnedRowPosition.bind(this));
    this.scrollView.addCleaner(this.destructor.bind(this));

    if (this.options.bg) {
        this.bg = new cc.Scale9Sprite(this.options.bg);
        this.bg.setLocalZOrder(-1);
        this.scrollView.addChild(this.bg);
    }

    if (this.styles.barPadding) {
        this.scrollView.setBarPadding(this.styles.barPadding);
    }

    this.scrollView.onResize = this.scrollView.createListener(() => {
        this.stop();

        const width = Array.isArray(this.styles.size) ? this.styles.size[cleverapps.resolution.mode].width : this.styles.size.width;
        let height = Array.isArray(this.styles.size) ? this.styles.size[cleverapps.resolution.mode].height : this.styles.size.height;

        if (this.options.height) {
            const h = this.options.height;
            height = Array.isArray(h) ? h[cleverapps.resolution.mode].height : h;
        }

        const tableWidth = this.table.getDirection() === cleverapps.UI.VERTICAL ? Math.max(this.container.width || 0, width) : width;
        this.scrollView.setContentSize2(tableWidth + 2 * this.styles.padding.x, height + 2 * this.styles.padding.y);

        this.scrollView.updateInnerContent();

        if (this.bg) {
            this.bg.setContentSize(this.scrollView.width, this.scrollView.height);
            this.bg.setPosition(this.scrollView.width / 2, this.scrollView.height / 2);
        }
    });

    this.scrollView.onResize();

    if (this.options.scroll !== undefined) {
        this.scrollView.scrollTo(this.options.scroll);
    } else {
        const pinnedRow = this.table.getPinnedRow();
        if (pinnedRow) {
            this.scrollView.scrollTo(this.rowViews.get(pinnedRow.id));
        }
    }
};

TableHandler.prototype.createRow = function (row) {
    const RowViewCtor = row.ViewCtor || this.options.rowConstructor || RowView;
    const rowView = new RowViewCtor(row, this.options.rowOptions || {});

    const animations = {
        ...TableHandler.DefaultAnimations,
        ...(row.animations || this.options.animations)
    };

    rowView.addRowAnimation = () => animations.addRowAnimation.call(rowView);
    rowView.moveRowAnimation = (endPosition) => animations.moveRowAnimation.call(rowView, endPosition);
    rowView.removeRowAnimation = () => animations.removeRowAnimation.call(rowView);

    return rowView;
};

TableHandler.prototype.showUp = function (rowId, index, callback) {
    const rowView = this.rowViews.get(rowId);

    rowView.x += rowView.width * 1.1;

    rowView.showUpAction = rowView.runAction(new cc.Sequence(
        new cc.DelayTime(index * 0.02 + 0.1),
        new cc.MoveBy(0.25, -rowView.width * 1.125, 0),
        new cc.MoveBy(0.1, rowView.width * 0.025, 0)
    ));
    rowView.showUpAction.setFinalize(callback);
};

TableHandler.prototype.add = function (row, index) {
    const rowView = this.createRow(row);
    this.rowViews.set(row.id, rowView);
    this.container.addChild(rowView, row.pinned ? 1000 : -index);

    const offset = this.scrollView.afterContainerSizeChanged(this._calcContainerSize());
    this.rowViews.forEach((r) => {
        r.setPosition(r.x + offset.x, r.y + offset.y);
    });

    rowView.setPosition(this._calculateRowPosition(rowView, index));

    this.scrollView.updateVisibility();

    rowView.addAction = rowView.runAction(rowView.addRowAnimation());

    cleverapps.tutorial.trigger("addRowView", row.id);
};

TableHandler.prototype.remove = function (rowId, callback, silent) {
    const rowView = this.rowViews.get(rowId);

    if (rowView.addAction) {
        rowView.stopAction(rowView.addAction);
        delete rowView.addAction;
    }

    const onRemoved = this.scrollView.createListener(() => {
        if (this.rowViews.get(rowId) === rowView) {
            this.rowViews.delete(rowId);
            rowView.removeFromParent();

            callback();
        }
    });

    if (silent) {
        onRemoved();
    } else {
        rowView.removeAction = rowView.runAction(rowView.removeRowAnimation()).setFinalize(onRemoved);
    }
};

TableHandler.prototype.onRowRemoved = function () {
    const offset = this.scrollView.afterContainerSizeChanged(this._calcContainerSize());
    this.rowViews.forEach((r) => {
        r.setPosition(r.x + offset.x, r.y + offset.y);
    });
    this.scrollView.updateVisibility();
};

TableHandler.prototype.updateRow = function (row) {
    const rowView = this.rowViews.get(row.id);
    if (rowView && rowView.updateRow) {
        rowView.updateRow(row);
    }
};

TableHandler.prototype.move = function (rowId, index) {
    const rowView = this.rowViews.get(rowId);
    const position = this._calculateRowPosition(rowView, index);

    if (rowView.moveAction) {
        rowView.stopAction(rowView.moveAction);
    }
    if (rowView.showUpAction) {
        rowView.stopAction(rowView.showUpAction);
        delete rowView.showUpAction;
    }
    const row = this.table.rows[index];
    rowView.setLocalZOrder(row.pinned ? 1000 : -index);

    rowView.setVisible(true);
    rowView.moveAction = rowView.runAction(rowView.moveRowAnimation(position));
};

TableHandler.prototype.stop = function () {
    this.scrollView.stopAllActions();

    [...this.table.rows].forEach((row) => {
        const view = this.rowViews.get(row.id);

        view.stopAllActions();
    });

    this.container.setContentSize2(this._calcContainerSize());

    this.table.rows.forEach((row, index) => {
        const view = this.rowViews.get(row.id);

        view.stopAllActions();
        view.setPosition(this._calculateRowPosition(view, index));
        view.setScale(1);
    });
};

TableHandler.prototype.scrollToRow = function (rowId, callback = () => {}) {
    const rowView = this.rowViews.get(rowId);

    const index = this.table.rows.findIndex((r) => r.id === rowId);
    this.scrollView.scrollTo(this._calculateRowPosition(rowView, index), TableHandler.SCROLL_DURATION, {
        easing: cc.easeInOut(1),
        callback
    });
};

TableHandler.prototype.updatePinnedRowPosition = function () {
    const pinnedRow = this.table.getPinnedRow();
    if (!pinnedRow) {
        return;
    }

    const pinnedRowView = this.rowViews.get(pinnedRow.id);
    if (!pinnedRowView) {
        return;
    }

    const moving = pinnedRowView.moveAction && !pinnedRowView.moveAction.isDone();
    const index = this.table.rows.findIndex((r) => r.id === pinnedRow.id);
    const position = this._calculateRowPosition(pinnedRowView, index);
    const pinnedY = this.getPinnedY(pinnedRowView);

    if (position.y < pinnedY) {
        if (!moving || pinnedRowView.y < pinnedY) {
            pinnedRowView.setPosition(position.x, pinnedY);
            pinnedRowView.setVisible(true);

            if (pinnedRowView.moveAction) {
                pinnedRowView.stopAction(pinnedRowView.moveAction);
                delete pinnedRowView.moveAction;
            }
        }
    } else if (!moving) {
        pinnedRowView.setPosition(position);
    }
};

TableHandler.prototype.getPinnedY = function (pinnedRowView) {
    return this.container.convertToNodeSpace(this.scrollView.convertToWorldSpace(cc.p(this._getInnerPadding().x, pinnedRowView.height / 2 + this.styles.pinnedDy))).y;
};

TableHandler.prototype.destructor = function () {
    runCleaners(this);
};

TableHandler.prototype._calculateRowPosition = function (rowView, index) {
    const margin = Array.isArray(this.styles.margin) ? this.styles.margin[cleverapps.resolution.mode] : this.styles.margin;
    const padding = this._getInnerPadding();

    if (this.table.getDirection() === cleverapps.UI.HORIZONTAL) {
        let sumWidth = 0;
        for (let i = 0; i < index; i++) {
            const view = this.rowViews.get(this.table.rows[i].id);
            sumWidth += view.width + margin;
        }
        return cc.p(padding.x + sumWidth + rowView.width / 2, padding.y + rowView.height / 2);
    }

    let sumHeightAbove = 0;
    for (let i = 0; i < index; i++) {
        const view = this.rowViews.get(this.table.rows[i].id);
        sumHeightAbove += view.height + margin;
    }

    return cc.p(padding.x + rowView.width / 2, this.container.height - padding.y - (rowView.height / 2) - sumHeightAbove);
};

TableHandler.prototype._getInnerPadding = function () {
    let padding = Array.isArray(this.styles.innerPadding) ? this.styles.innerPadding[cleverapps.resolution.mode] : this.styles.innerPadding;
    if (this.options.innerPadding) {
        padding = this.options.innerPadding.x ? this.options.innerPadding : cc.p(this.options.innerPadding, this.options.innerPadding);
    }
    return padding;
};

TableHandler.prototype._calcContainerSize = function () {
    const margin = Array.isArray(this.styles.margin) ? this.styles.margin[cleverapps.resolution.mode] : this.styles.margin;

    const views = [...this.rowViews.values()];

    let width, height;
    if (this.table.getDirection() === cleverapps.UI.HORIZONTAL) {
        const maxHeight = Math.max(
            ...views.map((node) => node.height)
        );
        width = views.map((rv) => rv.width).reduce((sum, w, idx, arr) => sum + w + (idx < arr.length - 1 ? margin : 0), 0);
        const stylesHeight = Array.isArray(this.styles.size) ? this.styles.size[cleverapps.resolution.mode].height : this.styles.size.height;
        height = views.length === 0 ? stylesHeight : maxHeight;
    } else {
        const maxWidth = Math.max(
            ...views.map((node) => node.width)
        );
        const stylesWidth = Array.isArray(this.styles.size) ? this.styles.size[cleverapps.resolution.mode].width : this.styles.size.width;
        width = views.length === 0 ? stylesWidth : maxWidth;
        height = views.map((rv) => rv.height).reduce((sum, h, idx, arr) => sum + h + (idx < arr.length - 1 ? margin : 0), 0);
    }

    const padding = this._getInnerPadding();

    return cc.size(width + 2 * padding.x, height + 2 * padding.y);
};

TableHandler.prototype._getAnchorRowIndex = function () {
    if (this.table.rows.length === 0) {
        return -1;
    }

    const firstView = this.rowViews.get(this.table.rows[0].id);
    const padding = this._getInnerPadding();
    const size = Array.isArray(this.styles.size) ? this.styles.size[cleverapps.resolution.mode] : this.styles.size;
    const margin = Array.isArray(this.styles.margin) ? this.styles.margin[cleverapps.resolution.mode] : this.styles.margin;

    if (this.table.getDirection() === cleverapps.UI.VERTICAL) {
        return Math.round((this.container.height / 2 - this.container.y - padding.y + size.height / 2)
            / (firstView.height + margin));
    }

    return Math.round((this.container.width / 2 - this.container.x - padding.x + size.width / 2)
        / (firstView.width + margin));
};

TableHandler.prototype._computeDeltaToPlaceRowAsAnchor = function (rowIndex) {
    if (this.table.rows.length === 0 || rowIndex === -1) {
        return cc.p(0, 0);
    }

    const firstView = this.rowViews.get(this.table.rows[0].id);
    const size = Array.isArray(this.styles.size) ? this.styles.size[cleverapps.resolution.mode] : this.styles.size;
    const margin = Array.isArray(this.styles.margin) ? this.styles.margin[cleverapps.resolution.mode] : this.styles.margin;

    if (this.table.getDirection() === cleverapps.UI.VERTICAL) {
        if (this.container.height <= this.scrollView.height) {
            return cc.p(0, 0);
        }
        const wanted = this.container.height / 2 - rowIndex * (firstView.height + margin) + size.height / 2;
        return cc.p(0, this.container.y - wanted);
    }

    if (this.container.width <= this.scrollView.width) {
        return cc.p(0, 0);
    }
    const wanted = this.container.width / 2 - rowIndex * (firstView.width + margin) + size.width / 2;
    return cc.p(this.container.x - wanted, 0);
};

TableHandler.SCROLL_DURATION = 0.6;

TableHandler.DefaultAnimations = {
    addRowAnimation: function () {
        this.setScale(0);

        return new cc.Sequence(
            new cc.DelayTime(0.1),
            new cc.ScaleTo(0.3, 1).easing(cc.easeBackOut())
        );
    },

    removeRowAnimation: function () {
        return new cc.ScaleTo(0.3, 0).easing(cc.easeBackIn());
    },

    moveRowAnimation: function (position) {
        return new cc.MoveTo(0.4, position).easing(cc.easeBackInOut());
    }
};

cleverapps.styles.DefaultTableStyles = {
    size: {
        width: 500,
        height: 732
    },

    margin: 4,
    padding: { x: 6, y: 2 },
    innerPadding: { x: 6, y: 6 },

    pinnedDy: -6,

    barPadding: {
        cornerPadding: 20,
        sidePadding: 5
    }
};
